import { useDispatch, useSelector } from "react-redux";
import { getGateways, getSensors, getSiteAnalytics, uploadHardware } from "../../Api Methods/Api";
import { useEffect, useState, useRef, useMemo } from "react";
import statClasses from "../Dashboard/Dashboard.module.css";
import classes from "./gateways.module.css";
import { Await, useNavigate } from "react-router-dom";
import { Line } from "react-chartjs-2";
import PadlocksList from "../Padlocks/padlocksList";
import DeviceLoadingGrid from "../../components/GridList/DeviceLoadingGrid";
import StatCard from "../../components/UI/Stats/StatCard";
import mixpanel from "mixpanel-browser";
import refreshBtn from '../../assets/portalAssets/refreshV2.png';
import { Button } from "primereact/button";
import { SelectButton } from "primereact/selectbutton";
import { Dialog } from "primereact/dialog";
import Papa from "papaparse";
import Lottie from "lottie-react";
import loadingAnim from "../../assets/animations/newLoading.json";

const toggleOptions = ["Battery", "Signal"];

// Plugin to show cluster indicators when multiple lines overlap
const clusterIndicatorPlugin = {
    id: 'clusterIndicator',
    afterDatasetsDraw(chart) {
        const { ctx, data } = chart;
        const threshold = 2; // Y-value threshold for considering points as overlapping

        if (!data.datasets || data.datasets.length < 2) return;

        const numPoints = data.labels?.length || 0;

        for (let pointIndex = 0; pointIndex < numPoints; pointIndex++) {
            // Group points by similar y-values at this x-position
            const clusters = [];

            data.datasets.forEach((dataset, datasetIndex) => {
                const value = dataset.data[pointIndex];
                if (value === null || value === undefined) return;

                const meta = chart.getDatasetMeta(datasetIndex);
                if (meta.hidden) return;

                const point = meta.data[pointIndex];
                if (!point) return;

                // Find existing cluster within threshold
                let foundCluster = clusters.find(c => Math.abs(c.avgValue - value) <= threshold);

                if (foundCluster) {
                    foundCluster.count++;
                    foundCluster.totalY += point.y;
                    foundCluster.avgValue = (foundCluster.avgValue * (foundCluster.count - 1) + value) / foundCluster.count;
                } else {
                    clusters.push({
                        count: 1,
                        x: point.x,
                        totalY: point.y,
                        avgValue: value
                    });
                }
            });

            // Draw badges for clusters with more than 3 points
            clusters.forEach(cluster => {
                if (cluster.count >= 3) {
                    const x = cluster.x;
                    const y = cluster.totalY / cluster.count;

                    ctx.save();
                    ctx.beginPath();
                    ctx.arc(x, y - 14, 9, 0, Math.PI * 2);
                    ctx.fillStyle = 'rgba(55, 55, 55, 0.8)';
                    ctx.fill();

                    ctx.fillStyle = '#fff';
                    ctx.font = 'bold 10px sans-serif';
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(cluster.count, x, y - 14);
                    ctx.restore();
                }
            });
        }
    }
};

const LineCard = ({ batteryData, signalData, onLegendClick, type }) => {
    const hasData = batteryData?.datasets?.length > 0;
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: hasData,
                onClick: onLegendClick || undefined,
                onHover: onLegendClick ? (e) => { e.native.target.style.cursor = 'pointer'; } : undefined,
                onLeave: onLegendClick ? (e) => { e.native.target.style.cursor = 'default'; } : undefined,
            },
            tooltip: { enabled: true }
        },
        scales: {
            x: { grid: { display: false } },
            y: { beginAtZero: true, max: 100, ticks: { stepSize: 20 } }
        }
    };
    return (
        <>
        {type === "Signal" && (
            !hasData ? (
                <p style={{ textAlign: "center", marginTop: "8rem", color: "#888888" }}>
                    No analytics data available for the last 14 days.
                </p>
            ) : (
                <div className={statClasses.chartCard}>
                    <p className={statClasses.chartTitle}>Signal Strength</p>
                    <div className={classes.barContainer}>
                        <Line data={signalData} options={options} plugins={[clusterIndicatorPlugin]} />
                    </div>
                </div>
            )
        )}

        {type === "Battery" && (
            !hasData ? (
                <p style={{ textAlign: "center", marginTop: "8rem", color: "#888888" }}>
                    No analytics data available for the last 14 days.
                </p>
            ) : (
                <div className={statClasses.chartCard}>
                    <p className={statClasses.chartTitle}>Battery Levels</p>
                    <div className={classes.barContainer}>
                        <Line data={batteryData} options={options} plugins={[clusterIndicatorPlugin]} />
                    </div>
                </div>
            )
        )}
        </>
    );
};

const RTSectionHeaderGateway = ({ title, onRefresh, bsToggle, setBSToggle }) => (
    <div className={statClasses.sectionHeaderTime} style={{ marginTop: "12px", marginBottom: "12px"}}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", justifyContent: "space-between", width: "100%" }}>
            <p className={statClasses.sectionTitle}>{title}</p>
            <div className={classes.headerBtns}>
                <SelectButton 
                    className={classes.filterToggle}
                    value={bsToggle} 
                    onChange={(e) => setBSToggle(e.value)} 
                    options={toggleOptions} />
                <img className={classes.refreshButton} onClick={onRefresh} src={refreshBtn} />
            </div>
        </div>
    </div>
);

const SkeletonBarChart = () => (
    <div className={statClasses.chartCard}>
        <div className={statClasses.skeletonBar} style={{ height: "14px", width: "40%", marginBottom: "16px" }} />
        <div className={statClasses.skeletonBar} style={{ height: "170px", width: "100%" }} />
    </div>
);

const Gateways = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const client = useSelector(state => state.auth.client);
    const token = useSelector(state => state.auth.token);
    const companyName = useSelector(state => state.auth.companyName);
    const apiDataRef = useRef({ token, client, companyName });
    apiDataRef.current = { token, client, companyName };

    const role = useSelector(state => state.auth.role);
    const apiData = {
        token,
        client
    }

    const [gateways, setGateways] = useState([])
    const [billableGateways, setBillableGateways] = useState(0)
    const [nonBillableGateways, setNonBillableGateways] = useState(0)
    const [criticalGateways, setCriticalGateways] = useState([])
    const [warningGateways, setWarningGateways] = useState([])
    const [healthyGateways, setHealthyGateways] = useState([]) 
    const [offlineGateways, setOfflineGateways] = useState([])
    const [staleGateways, setStaleGateways] = useState([])
    const [update, setUpdate] = useState(0)
    const filtering = ""
    const [isLoading, setIsLoading] = useState(true)
    const [counts, setCounts] = useState([])
    const [gatewayStats, setGatewayStats] = useState(null)
    const [activeTab, setActiveTab] = useState(0);
    const [isLoadingScheduleStats, setIsLoadingScheduleStats] = useState(true)
    const [bsToggle, setBSToggle] = useState("Battery")
    const [chartFilter, setChartFilter] = useState(null)
    
    const [uploadDialogVisible, setUploadDialogVisible] = useState(false);
    const [hardwareFile, setHardwareFile] = useState(null);
    const [imeiList, setImeiList] = useState([]);
    const [imeiParseError, setImeiParseError] = useState(null);
    const [loadingDialogVisible, setLoadingDialogVisible] = useState(false);

    const handleHardwareFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setHardwareFile(file);
        setImeiList([]);
        setImeiParseError(null);

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                if (!results.meta.fields?.includes("Manufacturer Serial")) {
                    setImeiParseError("No 'Manufacturer Serial' column found in the file.");
                    return;
                }
                const imeis = results.data
                    .map(row => row["Manufacturer Serial"])
                    .filter(v => typeof v === "string" && v.trim() !== "")
                    .map(v => v.trim());
                setImeiList(imeis);
            }
        });
    };

    const lastRefreshTime = useRef(0);
    const cooldown = 8000;

    const now = new Date();    
    const last14 = new Date();
    last14.setDate(now.getDate() - 14);
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const formatEventDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        return date.toLocaleDateString('en-US', { weekday: 'short' });
    };

    const uniqueIMEIs = useMemo(() => {
        if (!gatewayStats || gatewayStats.length === 0) return [];
        return [...new Set(gatewayStats.map(item => item.imei))];
    }, [gatewayStats]);

    // last readings and sectioned imeis
    const batteryStats = useMemo(() => {
        if (!gatewayStats || gatewayStats.length === 0) {
            return { critical: 0, warning: 0, healthy: 0, total: 0, criticalIMEIs: [], warningIMEIs: [], healthyIMEIs: [] };
        }

        const lastReadings = {};
        gatewayStats.forEach(item => {
            lastReadings[item.imei] = item;
        });

        const readings = Object.values(lastReadings);
        const criticalIMEIs = readings.filter(r => r.battery < 10).map(r => r.imei);
        const warningIMEIs = readings.filter(r => r.battery >= 10 && r.battery < 30).map(r => r.imei);
        const healthyIMEIs = readings.filter(r => r.battery >= 30).map(r => r.imei);

        return {
            critical: criticalIMEIs.length,
            warning: warningIMEIs.length,
            healthy: healthyIMEIs.length,
            total: readings.length,
            criticalIMEIs,
            warningIMEIs,
            healthyIMEIs
        };
    }, [gatewayStats]);

    const formatImei = (imei) => {
        const formattedImei = imei.slice(-4);
        return "x" + formattedImei;
    };

    const getGatewayChartData = () => {
        if (!gatewayStats || gatewayStats.length === 0) {
            return { batteryData: { labels: [], datasets: [] }, signalData: { labels: [], datasets: [] } };
        }

        const uniqueDates = [...new Set(gatewayStats.map(item => item.event_date))].sort();
        const formattedLabels = uniqueDates.map(formatEventDate);

        let filteredIMEIs = uniqueIMEIs;
        if (chartFilter === 'critical') filteredIMEIs = batteryStats.criticalIMEIs;
        else if (chartFilter === 'warning') filteredIMEIs = batteryStats.warningIMEIs;
        else if (chartFilter === 'healthy') filteredIMEIs = batteryStats.healthyIMEIs;

        const batteryDatasets = filteredIMEIs.map((imei, index) => {
            const hue = (index * 360 / Math.max(filteredIMEIs.length, 1)) % 360;
            const color = `hsl(${hue}, 70%, 55%)`;

            const data = uniqueDates.map(date => {
                const entry = gatewayStats.find(item => item.event_date === date && item.imei === imei);
                return entry?.battery ?? null;
            });

            return {
                label: formatImei(imei),
                originalImei: imei,
                data,
                borderColor: color,
                backgroundColor: `hsla(${hue}, 70%, 55%, 0.1)`,
                pointRadius: 4,
                pointHoverRadius: 6,
                fill: false,
                tension: 0.3,
                spanGaps: true,
            };
        });

        const signalDatasets = filteredIMEIs.map((imei, index) => {
            const hue = (index * 360 / Math.max(filteredIMEIs.length, 1)) % 360;
            const color = `hsl(${hue}, 70%, 45%)`;

            const data = uniqueDates.map(date => {
                const entry = gatewayStats.find(item => item.event_date === date && item.imei === imei);
                return entry?.signal ?? null;
            });

            return {
                label: formatImei(imei),
                originalImei: imei,
                data,
                borderColor: color,
                backgroundColor: `hsla(${hue}, 70%, 45%, 0.1)`,
                pointRadius: 4,
                pointHoverRadius: 6,
                fill: false,
                tension: 0.3,
                spanGaps: true,
            };
        });

        return {
            batteryData: { labels: formattedLabels, datasets: batteryDatasets },
            signalData: { labels: formattedLabels, datasets: signalDatasets }
        };
    };

    const handleGatewayLegendClick = (e, legendItem, legend) => {
        const datasetIndex = legendItem.datasetIndex;
        const dataset = legend.chart.data.datasets[datasetIndex];
        const imei = dataset.originalImei;
        navigate(`/Dashboard/Gateways/Gateway/${imei}`);
    };

    const { batteryData: batteryChartData, signalData: signalChartData } = getGatewayChartData();

    
    const handleRefreshStats = async () => {
        const currentTime = Date.now();
        if (currentTime - lastRefreshTime.current < cooldown) {
            setIsLoadingScheduleStats(true);
            setTimeout(() => {
                setIsLoadingScheduleStats(false);
            }, 2000);
            return;
        }
        lastRefreshTime.current = currentTime;

        setIsLoadingScheduleStats(true);
        setGatewayStats([]);
        const response = await getSiteAnalytics(apiDataRef.current, {
            siteUUID: "",
            groupName: apiDataRef.current.companyName || localStorage.getItem("companyName"),
            start_date: last14,
            end_date: now,
            analyticsType: "gateways"
        }, dispatch);
        setGatewayStats(response.data[0]);
        setIsLoadingScheduleStats(false);
    };

    //Pull data on mount
    useEffect(() => {
        const HandleGetGateways = async () => {
            const response = await getGateways(apiData, dispatch);
            console.log(response.data[0]);
            // console.log(JSON.parse(response.data[0][0].result));
            // const parsed = JSON.parse(response.data[0][0].result);
            // setGateways(parsed.data);
            // setCounts(parsed.counts)
            // console.log(counts);
            setBillableGateways(response.data[0].filter(lock => lock.billable == 1 && lock.billedToName === companyName))
            setNonBillableGateways(response.data[0].filter(lock => lock.billable == 0))
            setCriticalGateways(response.data[0].filter(lock => lock.battery <= 10))
            setWarningGateways(response.data[0].filter(lock => lock.battery > 10 && lock.battery < 30))
            setHealthyGateways(response.data[0].filter(lock => lock.battery >= 30))
            setOfflineGateways(response.data[0].filter(lock => lock.status == "stale"))
            setStaleGateways(response.data[0].filter(lock => lock.status == "potentiallyStale"))
            setGateways(response.data[0])
            setIsLoading(false)
        }

        
        const HandleGetGatewayStats = async () => {
            const response = await getSiteAnalytics(apiDataRef.current, {
                siteUUID: "",
                groupName: apiDataRef.current.companyName || localStorage.getItem("companyName"),
                start_date: last14,
                end_date: now,
                analyticsType: "gateways"
            }, dispatch);
            setGatewayStats(response.data[0]);
            setIsLoadingScheduleStats(false);
        } 
        
        HandleGetGateways();
        HandleGetGatewayStats();
   
        mixpanel.track("Gateways tab visited", {
            user: localStorage.getItem('email')
        });
    }, [update])

    const UploadHardware = async () => {
        const body = {
            "hardwareType": "GATEWAY",
            "hardwareID": imeiList
        }

        console.log(body);
        const response = await uploadHardware(apiData, body, dispatch)
        console.log(response);
        if (response.success){
            setLoadingDialogVisible(false)
        }
    }

    /*
    useEffect(() => {
        const currentRequestId = ++gatewayRequestId.current;
        setGatewayStats([]);
        setIsLoadingScheduleStats(true);
        const FetchGatewayStats = async () => {
            const response = await getSiteAnalytics(apiDataRef.current, {
                siteUUID: "",
                groupName: apiDataRef.current.companyName || localStorage.getItem("companyName"),
                start_date: last14,
                analyticsType: "gateways"
            }, dispatch);
            if (currentRequestId !== gatewayRequestId.current) return;
            const processedGateData = processGatewayData(response.data[0]);
            setGatewayStats(processedGateData);
            setIsLoadingScheduleStats(false);
        } 
        FetchGatewayStats();
    }) */


    return (
        <Await resolve={gateways}>
                <div style={{ width: "100%" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 290px))", gap: "var(--gap-lg)", marginBottom: "8px", justifyContent: "start" }}>
                        <StatCard title={"Gateways"} value={gateways.length} isLoading={isLoading} />
                        <StatCard title={"Offline gateways"} value={offlineGateways.length} isLoading={isLoading} />
                        <StatCard title={"Check-in missed"} value={staleGateways.length} isLoading={isLoading} />
                        <StatCard title={"Critical gateways (<10% battery)"} value={criticalGateways.length} isLoading={isLoading} />
                        <StatCard title={"Warning gateways (<30% battery)"} value={warningGateways.length} isLoading={isLoading} />
                        <StatCard title={"Healthy gateways (>30% battery)"} value={healthyGateways.length} isLoading={isLoading} />
                        <StatCard title={"Billable gateways"} value={billableGateways.length} isLoading={isLoading} />
                        <StatCard title={"Non-billable gateways"} value={nonBillableGateways.length} isLoading={isLoading} />
                    </div>
                   
                    {companyName === "EyeSpyFX" && (
                        <>
                            <div style={{ marginBottom: "12px" }}>
                                <Button
                                    label="Upload hardware"
                                    icon="pi pi-upload"
                                    severity="secondary"
                                    onClick={() => { setHardwareFile(null); setUploadDialogVisible(true); }}
                                />
                            </div>

                            <Dialog
                                header="Upload hardware"
                                visible={uploadDialogVisible}
                                onHide={() => { setUploadDialogVisible(false); setHardwareFile(null); setImeiList([]); setImeiParseError(null); }}
                                dismissableMask
                                style={{ width: "480px" }}
                            >
                                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                                    <p style={{ margin: 0, color: "#6e6e6e", fontSize: "15px" }}>
                                        Select a file to upload hardware data.
                                    </p>
                                    <input
                                        type="file"
                                        accept=".csv"
                                        onChange={handleHardwareFileChange}
                                    />
                                    {hardwareFile && !imeiParseError && imeiList.length > 0 && (
                                        <p style={{ margin: 0, color: "#6e6e6e", fontSize: "13px" }}>
                                            <strong>{hardwareFile.name}</strong> — {imeiList.length} IMEI{imeiList.length !== 1 ? "s" : ""} found
                                        </p>
                                    )}
                                    {imeiParseError && (
                                        <p style={{ margin: 0, color: "#e24c4c", fontSize: "13px" }}>
                                            {imeiParseError}
                                        </p>
                                    )}
                                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                                        <Button
                                            label="Cancel"
                                            severity="secondary"
                                            outlined
                                            onClick={() => { setUploadDialogVisible(false); setHardwareFile(null); setImeiList([]); setImeiParseError(null); }}
                                        />
                                        <Button
                                            label="Upload"
                                            icon="pi pi-check"
                                            severity="success"
                                            disabled={imeiList.length === 0}
                                            onClick={() => {
                                                console.log("IMEIs to upload:", imeiList);
                                                setUploadDialogVisible(false);
                                                setLoadingDialogVisible(true);
                                                UploadHardware()
                                            }}
                                        />
                                    </div>
                                </div>
                            </Dialog>

                            <Dialog
                                header="Uploading hardware..."
                                visible={loadingDialogVisible}
                                onHide={() => { setLoadingDialogVisible(false); window.location.reload(); }}
                                style={{ width: "30vw" }}
                                closable={false}
                            >
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                                    <Lottie animationData={loadingAnim} loop style={{ width: 250, height: 250 }} />
                                    <Button
                                        label="Close"
                                        severity="danger"
                                        onClick={() => { setLoadingDialogVisible(false); window.location.reload(); }}
                                    />
                                </div>
                            </Dialog>
                        </>
                    )}

                    <br></br>

                    <div className={classes.tabsWrapper}>
                        <div className={classes.tabs}>
                            {["Gateways", /*"Analytics"*/].map((tab, i) => (
                                <button
                                    key={tab}
                                    className={`${classes.tab} ${activeTab === i ? classes.active : ""}`}
                                    onClick={() => setActiveTab(i)}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>

                        <div className={classes.tabContent}>
                            {activeTab === 0 && (
                                isLoading ? (
                                    <div style={{ display: "flex", width: "100%" }}>
                                        <DeviceLoadingGrid CardCount={20} />
                                    </div>
                                ) : (
                                    <PadlocksList
                                        locks={gateways}
                                        filtering={filtering}
                                        update={update}
                                        setUpdate={setUpdate}
                                        isGateways={true}
                                    />
                                )
                            )}
                            
                            {activeTab === 1 && (
                                <div style={{ width: "100%" }}>
                                    <RTSectionHeaderGateway title="Gateways health - Last 14 Days" onRefresh={handleRefreshStats} bsToggle={bsToggle} setBSToggle={setBSToggle} />
                                    < br/>
                                    <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", marginBottom: "20px" }}>
                                        <div style={{ flex: 1, minWidth: "150px" }}>
                                            <StatCard title={"Critical"} value={batteryStats.critical} hasLabel={true} label={"< 10% Battery"} isLoading={isLoadingScheduleStats} onClick={batteryStats.critical > 0 ? () => setChartFilter(chartFilter === 'critical' ? null : 'critical') : undefined}/>
                                        </div>
                                        <div style={{ flex: 1, minWidth: "150px" }}>
                                            <StatCard title={"Warning"} value={batteryStats.warning} hasLabel={true} label={"< 30% Battery"} isLoading={isLoadingScheduleStats} onClick={batteryStats.warning > 0 ? () => setChartFilter(chartFilter === 'warning' ? null : 'warning') : undefined}/>
                                        </div>
                                        <div style={{ flex: 1, minWidth: "150px" }}>
                                            <StatCard title={"Healthy"} value={batteryStats.healthy} hasLabel={true} label={"> 30% Battery"} isLoading={isLoadingScheduleStats} onClick={batteryStats.healthy > 0 ? () => setChartFilter(chartFilter === 'healthy' ? null : 'healthy') : undefined}/>
                                        </div>
                                        <div style={{ flex: 1, minWidth: "150px" }}>
                                            <StatCard title={"Total"} value={batteryStats.total} isLoading={isLoadingScheduleStats} onClick={() => setChartFilter(null)}/>
                                        </div>
                                    </div>
                                    <div className={statClasses.fullbarWrapper}>
                                        {isLoadingScheduleStats ? <SkeletonBarChart /> : (
                                            <LineCard
                                                batteryData={batteryChartData}
                                                signalData={signalChartData}
                                                onLegendClick={handleGatewayLegendClick}
                                                type={bsToggle}
                                            /> 
                                        )}
                                    </div>
                                </div>
                            )}
                                
                        </div>
                    </div>
                </div>
           
        </Await>
    );
}


export default Gateways;